import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {find} from 'lodash';

import {PermissionCodeType} from 'app/general/types';
import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {sweetAPI} from 'app/store/sweet-api';
import {
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {
  FOLDERS_API_URL,
  FOLDERS_HASHID_API_URL,
  getFolderURLByHashid,
  TEAM_FOLDERS_API_URL,
} from 'app/urls';
import {gt, ngt} from 'app/utils/i18n';
import {FOLDER_TAGS, SEARCH_TAG} from 'app/utils/rtk-tags';

import {rehydrateFolderHashidTeams, rehydrateFoldersTeams} from './selectors';

import type {DashboardFoldersFetchParamsType} from 'app/documents/types';
import type {
  BasicTeamSerializedType,
  FolderHashidSerializedType,
  FolderSerializedType,
  ListResponseType,
  RawFolderHashidSerializedType,
  RawFolderSerializedType,
  UserJobSerializedType,
} from 'sweet-types';

export type FolderFetchParamsType = {
  parent?: string;
  team_id?: number;
  document_url?: string;
  permission?: PermissionCodeType;
  search?: string;
  page?: string;
  page_size?: number;
  ordering?: string;
  in_no_teams?: string;
  exclude_team_id?: string;
  tag?: string;
  filter?: string;
  private?: 'true' | 'false';
  visible_to_user?: string;
  search_fields?: 'name';
  folder?: string;
};

export type DeleteFolderProps = {id: string | number; name?: string};
export type MoveFolderProps = {
  hashid: string;
  folder: string;
  parent: string | null;
};
export type EditFolderProps = {id: string | number; name?: string};

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [FOLDER_TAGS.TAGS, FOLDER_TAGS.HASHID, SEARCH_TAG],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      folderByHashid: build.query<FolderHashidSerializedType, string>({
        query: (hashid) => ({
          url: getFolderURLByHashid(hashid),
        }),
        providesTags: (response) =>
          response != null
            ? [{type: FOLDER_TAGS.HASHID, id: response.hashid}]
            : [],

        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the folder.'),
        }),
        transformResponse: (response: RawFolderHashidSerializedType) => {
          return rehydrateFolderHashidTeams(
            response,
            response.related.basic_team
          );
        },
      }),

      folders: build.query<
        ListResponseType<FolderSerializedType>,
        FolderFetchParamsType | DashboardFoldersFetchParamsType
      >({
        query: (params = {}) => ({
          url: FOLDERS_API_URL,
          params: {page: 1, ...params},
        }),
        providesTags: providesTagsByURL(FOLDER_TAGS.TAGS),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: {
              fallback: () => gt('Unable to load folders.'),
              byStatus: {},
              silenceByStatus: {404: true},
            },
          })(args, api);
        },
        transformResponse: (
          response: ListResponseType<
            RawFolderSerializedType,
            {basic_team: Record<number, BasicTeamSerializedType>}
          >
        ) => {
          return rehydrateFoldersTeams(response, response.related?.basic_team);
        },
      }),

      deleteFolder: build.mutation<unknown, DeleteFolderProps>({
        query: ({id}) => ({
          url: `${FOLDERS_API_URL}${id}/`,
          method: 'DELETE',
        }),
        invalidatesTags: () => [{type: FOLDER_TAGS.TAGS, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onSuccess: ({name}) =>
              gt('Folder %s has been deleted.', name || ''),
            onError: () => gt('Unable to delete the folder.'),
          })(args, api);
        },
      }),

      moveFolder: build.mutation<
        UserJobSerializedType & {status: 'completed'},
        MoveFolderProps
      >({
        queryFn: (
          body,
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          const {hashid, folder, parent} = body;
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${FOLDERS_HASHID_API_URL}${hashid}/move/`,
                    method: 'POST',
                    body: {folder, parent},
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },

        invalidatesTags: () => [{type: FOLDER_TAGS.TAGS, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onSuccess: () => gt('Folder has been queued for moving.'),
            onError: () => gt('Unable to move the folder.'),
          })(args, api);
        },
      }),

      addTeamsToFolders: build.mutation<
        unknown,
        {folder: FolderSerializedType; urls: Array<string>}
      >({
        queryFn: ({folder, urls}, api, extraOptions, baseQuery) => {
          return baseQuery({
            url: TEAM_FOLDERS_API_URL,
            method: 'POST',
            body: urls.map((url) => ({
              folder: folder.url,
              team: url,
            })),
          });
        },
        invalidatesTags: () => [
          {type: SEARCH_TAG, id: 'list'},
          {type: FOLDER_TAGS.TAGS, id: 'list'},
        ],
        onQueryStarted: (args, api) => {
          showMessages({
            onSuccess: ({urls}) =>
              ngt(
                'Added team to the folder',
                'Added teams to the folder',
                urls.length
              ),
            onError: () =>
              ngt(
                'Unable to add the team to the folder at the moment',
                'Unable to add those teams to the folder at the moment',
                args.urls.length
              ),
          })(args, api);
        },
      }),

      removeTeamFromFolder: build.mutation<
        unknown,
        {
          teamURL: string;
          folder: FolderSerializedType;
        }
      >({
        queryFn: ({teamURL, folder}, api, extraOptions, baseQuery) => {
          const membership = find(
            folder.team_memberships,
            ({team}) => team.indexOf(teamURL) !== -1
          );

          if (membership != null) {
            return baseQuery({
              url: membership.url,
              method: 'DELETE',
            });
          } else {
            return {
              error: {
                status: 404,
                data: {message: gt('Team not in the folder')},
              },
            };
          }
        },
        onQueryStarted: showMessages({
          onSuccess: () => gt('Removed team from the folder'),
          onError: {
            fallback: (response) =>
              response?.message ?? gt('Could not remove team from the folder'),
            byStatus: {
              400: (response) =>
                response?.[0] ??
                gt('A folder must belong to at least one team'),
            },
          },
        }),
        invalidatesTags: () => [
          {type: FOLDER_TAGS.TAGS, id: 'list'},
          {type: SEARCH_TAG, id: 'list'},
        ],
      }),

      updateFolder: build.mutation<
        unknown,
        {name: string; folder: FolderSerializedType}
      >({
        query: ({folder, name}) => ({
          url: folder.url,
          method: 'PATCH',
          body: {name},
        }),
        invalidatesTags: () => [
          {type: FOLDER_TAGS.TAGS, id: 'list'},
          {type: SEARCH_TAG, id: 'list'},
        ],
        onQueryStarted: showMessages({
          onSuccess: () => gt('Folder updated successfully'),
          onError: () => gt('Unable to update folder'),
        }),
      }),

      createFolder: build.mutation<
        FolderSerializedType,
        {
          name: string;
          teamURLs: Array<string>;
          parent?: {url: string};
        }
      >({
        query: ({name, teamURLs, parent}) => ({
          url: FOLDERS_API_URL,
          method: 'POST',
          body: {name, teams: teamURLs, parent: parent?.url},
        }),
        invalidatesTags: (result, error, {parent}) => [
          {type: FOLDER_TAGS.TAGS, id: 'list'},
          {type: FOLDER_TAGS.HASHID, id: parent?.url},
          {type: SEARCH_TAG, id: 'list'},
        ],
        onQueryStarted: showMessages({
          onSuccess: () => gt('Folder created successfully'),
          onError: () => gt('Unable to create folder'),
        }),
      }),
    }),
  });

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useFoldersQuery: useFolders,
  useFolderByHashidQuery: useFolderByHashid,
  useLazyFoldersQuery: useLazyFolders,
  useCreateFolderMutation: useCreateFolder,
  useUpdateFolderMutation: useUpdateFolder,
  useDeleteFolderMutation: useDeleteFolder,
  useMoveFolderMutation: useMoveFolder,
  useAddTeamsToFoldersMutation: useAddTeamsToFolder,
  useRemoveTeamFromFolderMutation: useRemoveTeamsFromFolder,
} = extendedAPI;
